/**
 * End-user licence agreement, served publicly at `/legal/eula`.
 *
 * Written for the two audiences that actually read it: firm personnel deciding
 * what they may do with the platform, and the Intuit reviewer who requires a
 * licence agreement at a public URL before a QuickBooks Online app is
 * published. It is deliberately specific about what this software is — an
 * internal reporting layer over Clio and QuickBooks, not a system of record —
 * because that one fact governs most of the obligations below.
 *
 * It is not a substitute for review by the firm's counsel. Anything that is a
 * business decision rather than a statement about how the software behaves is
 * named as the Firm's, and the values it turns on live in `lib/legal.ts`.
 */
import { LegalDocument, type LegalSection } from '../components/LegalDocument'
import { LEGAL_CONTACT_EMAIL, LEGAL_EFFECTIVE_DATE, LEGAL_GOVERNING_STATE } from '../lib/legal'

const SECTIONS: readonly LegalSection[] = [
  {
    id: 'parties',
    title: 'This agreement, and who it binds',
    body: (
      <>
        <p>
          This End-User License Agreement (the <strong>Agreement</strong>) governs use of the
          Divorce with a Plan Finance Platform (the <strong>Platform</strong>), an internal
          application operated by Divorce with a Plan (the <strong>Firm</strong>).
        </p>
        <p>
          It is between the Firm and each individual granted an account (<strong>you</strong>).
          Signing in is your acceptance of it. If you do not accept it, do not sign in — there is
          nothing else you need to do, and no part of the Platform is reachable without a session.
        </p>
        <p>
          The Platform is not offered to the public, is not sold, and is not licensed to anyone
          outside the Firm and its authorised personnel and contractors. Nothing here creates a
          licence for a client, a counterparty, or any other third party.
        </p>
      </>
    ),
  },
  {
    id: 'licence',
    title: 'What you are licensed to do',
    body: (
      <>
        <p>
          For as long as your account is active, the Firm grants you a personal, non-exclusive,
          non-transferable, revocable licence to use the Platform for the Firm&rsquo;s own business
          purposes — reviewing the Firm&rsquo;s financial and practice data, and carrying out the
          tasks your role is granted.
        </p>
        <p>
          That is the whole of the grant. It conveys no ownership of the software, no rights in the
          Firm&rsquo;s data beyond reading and using it in the course of your work, and no right to
          use the Platform on behalf of anyone but the Firm.
        </p>
      </>
    ),
  },
  {
    id: 'accounts',
    title: 'Accounts, roles, and sessions',
    body: (
      <>
        <p>
          Access has two independent halves, and both are enforced by the server rather than by the
          screen in front of you:
        </p>
        <ul>
          <li>
            <strong>Identity.</strong> Sign-in is by Google, restricted to the Firm&rsquo;s Google
            Workspace domain. The Platform holds no passwords of its own.
          </li>
          <li>
            <strong>Authorisation.</strong> A Workspace account alone grants nothing. An
            administrator must have created your account and assigned it a role; that role
            determines which pages and which figures you may see.
          </li>
        </ul>
        <p>
          Your account is yours alone. Do not share it, do not let another person work under your
          session, and do not attempt to use an account or role that was not assigned to you.
          Sessions last twelve hours and end on sign-out.
        </p>
        <p>
          The Firm may change your role, suspend your account, or withdraw access at any time,
          including immediately and without notice where it believes the Platform or the
          Firm&rsquo;s data is at risk. Role changes take effect on your next request.
        </p>
      </>
    ),
  },
  {
    id: 'restrictions',
    title: 'What you may not do',
    body: (
      <>
        <p>You must not:</p>
        <ul>
          <li>
            Export, copy, publish, or transmit data from the Platform other than as your work
            requires and the Firm&rsquo;s policies permit. Client financial information and
            firm-wide figures are confidential in the ordinary course, and moving them to a personal
            device or account is a breach of this Agreement.
          </li>
          <li>
            Attempt to reach data your role is not granted — by editing a URL, by calling the API
            directly, or by any other means. Such attempts are refused, and they are recorded.
          </li>
          <li>
            Reverse engineer, decompile, or attempt to derive the source of the Platform, except
            where the Firm employs or engages you to do exactly that.
          </li>
          <li>
            Interfere with the Platform&rsquo;s operation — probing, load testing, scraping,
            disabling a control, or circumventing a rate limit — without the Firm&rsquo;s written
            authorisation.
          </li>
          <li>
            Use the Platform to store or transmit anything unlawful, or use it in breach of the
            Firm&rsquo;s professional or ethical obligations, including those concerning client
            funds and client confidences.
          </li>
          <li>
            Connect the Platform to a QuickBooks Online company, a Clio account, or any other system
            that the Firm does not own or is not authorised to connect.
          </li>
        </ul>
      </>
    ),
  },
  {
    id: 'sources',
    title: 'Connected services: QuickBooks Online, Clio, and Google',
    body: (
      <>
        <p>
          The Platform reads from third-party systems the Firm subscribes to, and it holds only
          copies. Your use of those systems is governed by the Firm&rsquo;s agreements with their
          providers, not by this one:
        </p>
        <ul>
          <li>
            <strong>QuickBooks Online</strong> (Intuit Inc.) — the chart of accounts, invoices,
            payments, vendors, and bills. The Platform&rsquo;s access is read-only: it queries the
            QuickBooks Online Accounting API and never writes to, alters, or deletes anything in the
            Firm&rsquo;s QuickBooks company. Use of QuickBooks itself is subject to Intuit&rsquo;s
            own terms of service and privacy statement.
          </li>
          <li>
            <strong>Clio</strong> — clients, matters, time entries, invoices, client payments, and
            trust ledger entries. Also read-only.
          </li>
          <li>
            <strong>Google Workspace</strong> — sign-in, and delivery of alert email where you have
            asked for it.
          </li>
        </ul>
        <p>
          Only an administrator holding the integrations grant may connect or disconnect a source.
          Disconnecting one stops further reading from it; data already synced remains in the
          Platform until it is removed under the Firm&rsquo;s retention practice.
        </p>
      </>
    ),
  },
  {
    id: 'not-a-record',
    title: 'The Platform is not the book of record, and gives no advice',
    body: (
      <>
        <p>
          Every figure shown is derived from a scheduled copy of data held elsewhere, so it can be
          stale. The Platform states when each source last synced for exactly that reason, and a
          figure should be read together with that timestamp.
        </p>
        <p>
          The Firm&rsquo;s books of record remain QuickBooks Online and Clio. Where the Platform and
          a source disagree, the source governs. Do not rely on the Platform for a filing, a client
          statement, a trust account reconciliation of record, or any other purpose where the
          authoritative figure is required — take that from the source system.
        </p>
        <p>
          Nothing in the Platform is accounting, tax, legal, or investment advice, and no output of
          it substitutes for the judgement of a qualified professional.
        </p>
      </>
    ),
  },
  {
    id: 'confidentiality',
    title: 'Confidentiality and client information',
    body: (
      <>
        <p>
          The Platform contains client names, matter information, invoices, payments, and client
          trust balances. That information is confidential, and in the Firm&rsquo;s hands much of it
          is subject to professional duties that survive the end of your engagement with the Firm.
        </p>
        <p>
          Treat everything you see here as confidential unless the Firm has told you otherwise. Your
          obligations under the Firm&rsquo;s employment, engagement, and confidentiality agreements
          apply in full to the Platform and continue after your access ends.
        </p>
        <p>
          Every change to financial data, and every refused request, is recorded against the account
          that made it in an audit log that cannot be edited or deleted from within the Platform.
        </p>
      </>
    ),
  },
  {
    id: 'availability',
    title: 'Availability, changes, and support',
    body: (
      <>
        <p>
          The Platform is provided as it stands, without any service level commitment. The Firm may
          change, suspend, or discontinue any part of it — a page, a figure, a connected source, or
          the Platform as a whole — at any time.
        </p>
        <p>
          Scheduled syncs, alerts, and digests depend on third-party systems the Firm does not
          control. An outage or a rate limit at Intuit, Clio, Google, or Slack will delay data and
          notifications, and the Firm is not responsible for that delay.
        </p>
      </>
    ),
  },
  {
    id: 'warranty',
    title: 'No warranties',
    body: (
      <>
        <p>
          The Platform is provided <strong>&ldquo;as is&rdquo;</strong> and{' '}
          <strong>&ldquo;as available&rdquo;</strong>. To the fullest extent permitted by law, the
          Firm disclaims all warranties, express or implied, including any implied warranty of
          merchantability, fitness for a particular purpose, non-infringement, accuracy, or
          uninterrupted or error-free operation.
        </p>
        <p>
          The Firm does not warrant that any figure shown is accurate, current, or complete, or that
          it reconciles to a source system at any given moment.
        </p>
      </>
    ),
  },
  {
    id: 'liability',
    title: 'Limitation of liability',
    body: (
      <>
        <p>
          To the fullest extent permitted by law, the Firm is not liable for any indirect,
          incidental, special, consequential, or exemplary damages, or for any loss of profits,
          revenue, data, or goodwill, arising out of or relating to your use of the Platform — even
          if the Firm has been advised of the possibility of such damages.
        </p>
        <p>
          Nothing in this section limits liability that cannot be limited by law, and nothing in it
          affects rights or obligations arising under your employment or engagement with the Firm.
        </p>
      </>
    ),
  },
  {
    id: 'term',
    title: 'Term and termination',
    body: (
      <>
        <p>
          This Agreement applies from your first sign-in until your access ends. It ends
          automatically when your account is deactivated or your engagement with the Firm ends, and
          the Firm may end it at any time.
        </p>
        <p>
          On termination, stop using the Platform and destroy or return any data you hold from it.
          The sections on confidentiality, no warranties, limitation of liability, and governing law
          survive termination.
        </p>
      </>
    ),
  },
  {
    id: 'law',
    title: 'Governing law',
    body: (
      <p>
        This Agreement is governed by the laws of the State of {LEGAL_GOVERNING_STATE}, without
        regard to its conflict-of-laws rules. The state and federal courts sitting in{' '}
        {LEGAL_GOVERNING_STATE} have exclusive jurisdiction over any dispute arising out of it.
      </p>
    ),
  },
  {
    id: 'changes',
    title: 'Changes to this Agreement, and how to reach us',
    body: (
      <>
        <p>
          The Firm may revise this Agreement. The effective date above changes when it does, and
          continuing to sign in after a revision is acceptance of it. Where a change materially
          reduces what you may do, the Firm will say so rather than leave it to be discovered.
        </p>
        <p>
          Questions about this Agreement go to{' '}
          <a href={`mailto:${LEGAL_CONTACT_EMAIL}`}>{LEGAL_CONTACT_EMAIL}</a>.
        </p>
      </>
    ),
  },
]

export function Eula() {
  return (
    <LegalDocument
      title="End-User License Agreement"
      effectiveDate={LEGAL_EFFECTIVE_DATE}
      standfirst={
        <p>
          The Divorce with a Plan Finance Platform is an internal application for the Firm&rsquo;s
          own personnel. It reads the Firm&rsquo;s financial and practice data from QuickBooks
          Online and Clio and presents it in one place; it is not a system of record, it is not sold
          or offered to the public, and access is granted per person and per role. These are the
          terms on which you may use it.
        </p>
      }
      sections={SECTIONS}
      related={{ to: '/legal/privacy', label: 'the Privacy Policy' }}
    />
  )
}
